import store from '@/store'
import { getEcho, leaveChannel } from './echo'

export const tenantChannel = (suffix) => `${store.state.tenant}.${suffix}`

// Suscriptores vivos por canal (privado o público).
//
// laravel-echo cachea el canal por nombre: dos componentes que se suscriben a
// `user.{uuid}` (EventContainer y RealTimeConnections lo hacen) reciben el
// MISMO objeto. Antes, el primero que se desmontaba llamaba a echo.leave() y
// le mataba el canal al otro: las notificaciones dejaban de llegar sin ningún
// error. Ahora cada suscripción retira solo sus propios handlers, y el canal se
// abandona recién cuando no queda nadie.
const subscribers = new Map()

export const subscribePrivate = (suffix, listeners) => {
  const name = tenantChannel(suffix)
  return listenOn(name, getEcho().private(name), listeners)
}

// Canal público: suscribirse no pasa por /broadcasting/auth. Solo para datos
// que cualquiera puede ver (conteos, no personas).
export const subscribePublic = (suffix, listeners) => {
  const name = tenantChannel(suffix)
  return listenOn(name, getEcho().channel(name), listeners)
}

const listenOn = (name, channel, listeners) => {
  const bound = Object.keys(listeners).map((event) => [`.${event}`, listeners[event]])
  bound.forEach(([event, handler]) => channel.listen(event, handler))

  // Si Echo se recreó (logout → login), el conteo viejo ya no aplica.
  const entry = subscribers.get(name)
  const count = entry && entry.channel === channel ? entry.count : 0
  subscribers.set(name, { channel, count: count + 1 })

  let active = true
  return () => {
    if (!active) {
      return
    }
    active = false
    bound.forEach(([event, handler]) => channel.stopListening(event, handler))

    const current = subscribers.get(name)
    if (!current || current.channel !== channel) {
      return
    }
    if (current.count > 1) {
      subscribers.set(name, { channel, count: current.count - 1 })
      return
    }
    subscribers.delete(name)
    leaveChannel(name)
  }
}

export const subscribePresence = (suffix, listeners = {}) => {
  const name = tenantChannel(suffix)
  const channel = getEcho().join(name)

  if (listeners.here) {
    channel.here(listeners.here)
  }
  if (listeners.joining) {
    channel.joining(listeners.joining)
  }
  if (listeners.leaving) {
    channel.leaving(listeners.leaving)
  }

  return () => leaveChannel(name)
}

export const subscribeChatChannel = (channelId, listeners) =>
  subscribePrivate(`chat.${channelId}`, listeners)

export const subscribeThread = (threadId, listeners) =>
  subscribePrivate(`thread.${threadId}`, listeners)

export const subscribeUserChannel = (userUuid, listeners) =>
  subscribePrivate(`user.${userUuid}`, listeners)

export const subscribeAnnouncements = (listeners) =>
  subscribePrivate('announcements', listeners)

export const subscribeHybrid = (listeners) => subscribePrivate('hybrid', listeners)

export const subscribeOnlinePresence = (listeners) =>
  subscribePresence('online', listeners)

export const subscribeRoomPresence = (slug, listeners) =>
  subscribePresence(`room.${slug}`, listeners)

// A room join/leave; clients that use the whole location map refetch
// GET /event/locations.
export const subscribeLocations = (listeners) =>
  subscribePrivate('locations', listeners)

// `LocationCounts`: people per event and room, broadcast by the backend every
// few seconds when they change.
export const subscribeLocationCounts = (listeners) =>
  subscribePublic('location-counts', listeners)
